import React, { FC, InputHTMLAttributes, useMemo, useState } from 'react';
import styled from 'styled-components';

const SLIDER_THUMB_WIDTH = 26;
const SLIDER_THUMB_HALF_WIDTH = SLIDER_THUMB_WIDTH / 2;

const getThumbCenterPosition = (progress: number) => `calc(${progress}% + ${SLIDER_THUMB_HALF_WIDTH - (SLIDER_THUMB_WIDTH * progress) / 100}px)`;

const SliderContainer = styled.div`
    padding: 8px 0;
    display: flex;
    align-items: center;
    width: 100%;
    min-width: 0;
`;

const SliderControl = styled.div`
    position: relative;
    width: 100%;
    min-width: 0;
    height: 18px;
`;

const SliderTrack = styled.div<{ $progress: number }>`
    position: absolute;
    left: 0;
    right: 0;
    top: 7px;
    height: 4px;
    border-radius: 999px;
    background: ${(props) => {
        const thumbCenter = getThumbCenterPosition(props.$progress);
        return `linear-gradient(to right, ${props.theme.app.primary} 0%, ${props.theme.app.primary} ${thumbCenter}, #e5e7eb ${thumbCenter}, #e5e7eb 100%)`;
    }};
    pointer-events: none;
`;

const SliderThumbValue = styled.span<{ $progress: number }>`
    position: absolute;
    top: 0;
    left: ${(props) => getThumbCenterPosition(props.$progress)};
    transform: translateX(-50%);
    height: 18px;
    line-height: 18px;
    min-width: 26px;
    padding: 0 5px;
    text-align: center;
    font-size: 10px;
    background-color: ${(props) => props.theme.app.primary};
    color: #fff;
    border-radius: 999px;
    box-sizing: border-box;
    pointer-events: none;
    z-index: 2;
`;

const SliderInput = styled.input`
    position: relative;
    z-index: 3;
    width: 100%;
    height: 18px;
    margin: 0;
    padding: 0;
    appearance: none;
    background: transparent;
    cursor: pointer;

    &:disabled {
        cursor: not-allowed;
        opacity: 0.55;
    }

    &::-webkit-slider-runnable-track {
        height: 4px;
        background: transparent;
        border: 0;
    }

    &::-moz-range-track {
        height: 4px;
        background: transparent;
        border: 0;
    }

    &::-webkit-slider-thumb {
        appearance: none;
        width: ${SLIDER_THUMB_WIDTH}px;
        height: 18px;
        margin-top: -7px;
        border: 0;
        border-radius: 999px;
        background: transparent;
    }

    &::-moz-range-thumb {
        width: ${SLIDER_THUMB_WIDTH}px;
        height: 18px;
        border: 0;
        border-radius: 999px;
        background: transparent;
    }

    &:focus-visible {
        outline: none;

        & + ${SliderThumbValue} {
            box-shadow: 0 0 0 2px ${(props) => props.theme.app.primary_light};
        }
    }
`;

const StyledCaption = styled.span`
    display: inline-block;
    font-size: 12px;
    line-height: 16px;
    color: rgb(68, 90, 100);
    margin-top: 4px;
`;

type NativeSliderProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'defaultValue' | 'onChange' | 'onDragEnd' | 'type' | 'value'>;

type SliderValue = number | readonly number[];

type SliderProps = NativeSliderProps & {
    caption?: string;
    className?: string;
    defaultValue?: SliderValue;
    max?: number;
    min?: number;
    onAfterChange?: (value: SliderValue) => void;
    onChange?: (value: SliderValue) => void;
    step?: number;
    value?: SliderValue;
};

const getNumberValue = (value: SliderValue | undefined, fallback: number) => {
    if (Array.isArray(value)) return Number(value[0] ?? fallback);
    return Number(value ?? fallback);
};

const getProgress = (value: number, min: number, max: number) => {
    if (max <= min) return 0;
    return Math.min(100, Math.max(0, ((value - min) / (max - min)) * 100));
};

const Slider: FC<SliderProps> = ({ caption, className, defaultValue, max = 100, min = 0, onAfterChange, onChange, step = 1, value, ...props }) => {
    const [internalValue, setInternalValue] = useState(() => getNumberValue(defaultValue, min));
    const currentValue = getNumberValue(value, internalValue);
    const progress = useMemo(() => getProgress(currentValue, Number(min), Number(max)), [currentValue, max, min]);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const nextValue = Number(event.target.value);
        if (value == null) setInternalValue(nextValue);
        onChange?.(nextValue);
    };

    const handleAfterChange = (event: React.SyntheticEvent<HTMLInputElement>) => {
        onAfterChange?.(Number(event.currentTarget.value));
    };

    return (
        <>
            <SliderContainer className={className}>
                <SliderControl>
                    <SliderTrack $progress={progress} />
                    <SliderInput
                        {...props}
                        type='range'
                        min={min}
                        max={max}
                        step={step}
                        value={currentValue}
                        onBlur={handleAfterChange}
                        onChange={handleChange}
                        onMouseUp={handleAfterChange}
                        onTouchEnd={handleAfterChange}
                    />
                    <SliderThumbValue $progress={progress}>{currentValue}</SliderThumbValue>
                </SliderControl>
            </SliderContainer>
            {caption && <StyledCaption>{caption}</StyledCaption>}
        </>
    );
};

export default Slider;
